import { MenuProps } from 'antd';
import { TFunction } from 'i18next';

import { AppDispatch } from 'app/store';

import jobsService from 'features/jobs/jobsService';
import { upsertJob } from 'features/jobs/jobsSlice';

import { GTMEventLogger } from 'utils/analytics';
import { notify } from 'utils/toast/createToast';

import { StageTransitionNotification } from 'types/jobs';

import { AlertType, GTMEvents, JobStatus } from 'constants/enums';

import { ReactComponent as CloseSquare } from 'assets/icons/close-square-filled.svg';
import { ReactComponent as Pause } from 'assets/icons/pause.svg';
import { ReactComponent as TickSquare } from 'assets/icons/tick-square-filled.svg';
import { ReactComponent as UserSearch } from 'assets/icons/user-search-filled.svg';

import styles from './JobStatus.module.scss';

export const stageClassNames: Record<JobStatus, string> = {
  [JobStatus.Active]: styles.active,
  [JobStatus.Completed]: styles.completed,
  [JobStatus.Inactive]: styles.inactive,
  [JobStatus.InReview]: styles.inReview
  //[JobStatus.Incomplete]: styles.Incomplete
};

export const stageIcons: Record<
  JobStatus,
  React.FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  [JobStatus.Active]: TickSquare,
  [JobStatus.Completed]: CloseSquare,
  [JobStatus.Inactive]: Pause,
  [JobStatus.InReview]: UserSearch
  //[JobStatus.Incomplete]: TickSquare
};

export const disabledStageTooltips = [
  { stage: JobStatus.InReview, text: 'Job under review by Joblio' }
];

export const JOB_STAGE_TRANSITION_NOTIFICATIONS: StageTransitionNotification<JobStatus>[] =
  [
    {
      from: JobStatus.Inactive,
      // on UI and by request we are updating job to active, because this is what the user wants,
      // but BE changes status to in_review, and we show this warning
      to: JobStatus.InReview,
      message: 'stageSelector.jobTransition.review'
    },
    {
      from: JobStatus.Inactive,
      to: JobStatus.Completed,
      message: 'stageSelector.jobTransition.closed'
    },
    {
      from: JobStatus.Active,
      to: JobStatus.Completed,
      message: 'stageSelector.jobTransition.closed'
    },
    {
      from: JobStatus.Active,
      to: JobStatus.Inactive,
      message: 'stageSelector.jobTransition.inactive'
    },
    {
      from: JobStatus.Completed,
      to: JobStatus.InReview,
      message: 'stageSelector.jobTransition.review'
    }
  ];

export const handleChangeStage =
  (
    id: string,
    dispatch: AppDispatch,
    translate: TFunction<'common', undefined>
  ): MenuProps['onClick'] =>
  async ({ key }) => {
    const status = key as JobStatus;
    try {
      const oldJob = await jobsService.getJobById(id);

      await jobsService.changeJobStatus(id, status);

      const updatedJob = await jobsService.getJobById(id);

      dispatch(
        upsertJob({
          ...updatedJob
        })
      );

      const transitionNotification = JOB_STAGE_TRANSITION_NOTIFICATIONS.find(
        (el) => el.from === oldJob.status && el.to === updatedJob.status
      )?.message;

      GTMEventLogger(GTMEvents.JobListStatusEdit);

      if (transitionNotification) {
        notify({
          type: AlertType.Success,
          description: translate(transitionNotification)
        });
      }
    } catch (error) {
      notify({
        message: translate('error.message.operationFailed'),
        description: translate('error.message.updateJobStatus')
      });
    }
  };

export const configStyles = styles;
